import { Show } from 'react-admin';
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Box, Typography, Paper, Chip, LinearProgress, Alert, Grid, IconButton, Button } from '@mui/material';
import { Warning, CheckCircle, Schedule, HealthAndSafety, Security, Close, VerifiedUser } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { ExpirySparkline, STATUS_COLORS, CERTIFICATION_STATUS } from './constants';
import { OHSWorkflowStatus } from './OHSWorkflowStatus';
import type { OHSCertificationRecord } from '@/types/entities';

const CertificationShowContent = ({ record }: { record: OHSCertificationRecord | undefined }) => {
  const navigate = useNavigate();
  const [now] = useState(() => Date.now());
  if (!record) return null;
  const daysUntilExpiry = record?.expiryDate ? Math.ceil((new Date(record.expiryDate).getTime() - now) / (1000 * 60 * 60 * 24)) : null;
  const isExpiring = daysUntilExpiry !== null && daysUntilExpiry <= 60 && daysUntilExpiry > 0;
  const isExpired = daysUntilExpiry !== null && daysUntilExpiry <= 0;
  const validityPct = record.issueDate && record.expiryDate && daysUntilExpiry !== null ? Math.min(100, Math.round((1 - daysUntilExpiry / 365) * 100)) : 0;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={() => navigate('/ohs-certifications')} size="small" sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </Box>
      <PageDescription entity="OHS Certification" description="An occupational health and safety certification (e.g., first aid, fire safety). Tracks validity and renewal." icon={<VerifiedUser />} />
      {isExpired && <Alert severity="error" sx={{ mb: 2 }} icon={<Warning />}>This certification has expired — renewal required immediately</Alert>}
      {isExpiring && !isExpired && <Alert severity="warning" sx={{ mb: 2 }} icon={<Schedule />}>This certification expires in {daysUntilExpiry} days</Alert>}
      {record.status === 'Active' && !isExpiring && !isExpired && <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>Certification active and valid</Alert>}

      <OHSWorkflowStatus
        entityId={record.id || ''}
        entityType="certification"
        currentState={record.status}
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Employee', value: record.employee?.id, bold: true },
          { label: 'Certification Type', value: record.certificationLevel },
          { label: 'Status', value: record.status, chip: true },
          { label: 'Certified At', value: record.issueDate },
          { label: 'Expiry Date', value: record.expiryDate, warning: isExpiring || isExpired },
          { label: 'Provider', value: record.issuingBody },
          { label: 'Days Remaining', value: daysUntilExpiry !== null ? `${daysUntilExpiry}d` : '—', highlight: daysUntilExpiry !== null && daysUntilExpiry <= 30 },
        ].map(f => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">{f.label}</Typography>
              {f.chip ? (
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={f.value} size="small" color={STATUS_COLORS[record.status]} />
                </Box>
              ) : (
                <Typography variant="body1" sx={{ fontWeight: f.bold ? 700 : 400, mt: 0.5, color: (f as { warning?: boolean; highlight?: boolean }).warning ? 'error.main' : (f as { warning?: boolean; highlight?: boolean }).highlight ? 'warning.main' : 'inherit' }}>{f.value ?? '—'}</Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Certification Validity</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Valid period</Typography>
                  <Typography variant="caption">{Math.max(0, 365 - (daysUntilExpiry || 0))} / 365 days</Typography>
                </Box>
                <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, validityPct))} sx={{ height: 10, borderRadius: 1 }} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {record.status === 'Expired' && <Chip label="Renewal Required" color="error" icon={<Warning />} />}
              {isExpiring && <Chip label="Expiring Soon" color="warning" icon={<Schedule />} />}
              {record.status === 'Active' && !isExpiring && <Chip label="Active" color="success" icon={<CheckCircle />} />}
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Quick Actions</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button component={Link} to="/workplace-injuries" variant="outlined" size="small" startIcon={<Warning />}>
                View Incidents
              </Button>
              <Button component={Link} to="/ohs-representatives" variant="outlined" size="small" startIcon={<Security />}>
                View Representatives
              </Button>
              <Button component={Link} to="/employees" variant="outlined" size="small">
                View Employee
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export const CertificationShow = () => (
  <Show render={(props) => {
    const record = props.record as OHSCertificationRecord | undefined;
    return <CertificationShowContent record={record} />;
  }} />
);
